import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import { FilterChangedEvent, GridApi } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'

import 'ag-grid-enterprise'
import { useTheme } from '@mui/material/styles'
import { constants } from '../../common/constants'
import { riskByCptyColDef } from '../../common/riskByCptyColDef'
import { exportMultipleSheetsAsExcel } from 'ag-grid-enterprise'
import _ from 'lodash'
import { getDivHeight } from '../../common/commonFunctions'


interface AgGridProps {
  getGridRef?: any
  getGridApi?: any
  doesExternalFilterPass?: any
  isExternalFilterPresent?: any
  getContextMenuItems?: any
  refresh: any
  rowData: any
  onAddEvent: () => void
  onSave: () => void
  onCancel: () => void
  isSaveDisabled: boolean
  onCellChange?: any
  isAddDisabled: boolean
  isCancelDisabled: boolean
}

const RiskByCPTYGrid = ({
  getGridApi,
  doesExternalFilterPass,
  isExternalFilterPresent,
  getContextMenuItems,
  getGridRef,
  rowData,
  refresh,
  onCellChange,
  onSave,
  onCancel,
  isSaveDisabled,
  onAddEvent,
  isAddDisabled,
  isCancelDisabled
}: AgGridProps) => {
  const gridRef = useRef<any>(null)
  const theme = useTheme()
  const [columnDefs] = useState<any>([...riskByCptyColDef])
  const [gridTheme, setGridTheme] = useState<string>(
    theme.palette.mode === 'dark' ? ' ag-theme-balham-dark' : ' ag-theme-balham'
  )
  useEffect(() => {
    setGridTheme(theme.palette.mode === 'dark' ? ' ag-theme-balham-dark' : ' ag-theme-balham')
  }, [theme.palette.mode])
  const topDivRef = useRef(null)
  const [isGridVisible, setIsGridVisible] = useState<boolean>(false)
  const [globalSearch, setGlobalSearch] = useState<string>()

  // const [isAddDisabled, setIsAddDisabled] = useState<boolean>(false)
  // const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true)
  // const [isCancelDisabled, setIsCancelDisabled] = useState<boolean>(true)
  let gridApi: any

  useEffect(() => {
    setIsGridVisible(true)
  }, [topDivRef?.current])

  const defaultColDef = useMemo<any>(() => {
    return {
      flex: 1,
      sortable: true,
      filter: true,
      floatingFilter: false,
      suppressFilterButton: false,
      editable: false,
      resizable: true,
      headerCheckboxSelectionFilteredOnly: true,
      menuTabs: [constants.FilterMenuTab],
      tooltipShowDelay: 10,
      tooltipMouseTrack: true,
      suppressKeyboardEvent: (params: any) => {
        const isTabKey = params?.event?.key === constants.Tab
        if (isTabKey) gridRef?.current?.api?.stopEditing()

        return false
      }
    }
  }, [])

  const onCellValueChanged = useCallback(async (event: any) => {
    event.value = event?.value && _.isString(event?.value) ? event?.value?.toUpperCase() : event?.value

    await onCellChange(event.data, event?.value, event.column)

    // if (event?.data?.isRowUpdated) {
    //   setIsAddDisabled(true)
    //   setIsSaveDisabled(false)
    //   setIsCancelDisabled(false)
    // }
  }, [])

  const onExcelExport = () => {
    const spreadsheets = []
    spreadsheets.push(
      gridRef?.current?.api?.getSheetDataForExcel?.({
        sheetName: 'Risk By CPTY'
      })
    )

    exportMultipleSheetsAsExcel({
      data: spreadsheets,
      fileName: 'risk-by-cpty.xlsx'
    })
  }

  const refreshEvent = () => {
    refresh()

    // setIsAddDisabled(false)
    // setIsSaveDisabled(true)
    // setIsCancelDisabled(true)
  }

  useEffect(() => {
    gridApi = gridRef?.current?.api

    const gridColumnApi = gridRef?.current?.columnApi
    aggRowCal(gridApi, gridColumnApi)
    const refreshParams: any = {
      force: true,
      suppressFlash: true
    }
    gridRef?.current?.api?.refreshCells(refreshParams)
  }, [rowData])

  const onGridReady = (params: any) => {
    params.api.sizeColumnsToFit()
    gridApi = params.api

    const gridColumnApi = params.columnApi
    aggRowCal(gridApi, gridColumnApi)

    // Emmits the gridApi refrence to the parent component
    if (getGridApi) {
      getGridApi(gridApi, gridColumnApi)
    }
    getGridRef(gridRef)
  }

  const onFilterChanged = (params: FilterChangedEvent) => {
    const { api, columnApi } = params
    aggRowCal(api, columnApi)
  }

  /**
   * Method counts the unique value for given attribute (for filtered values also)
   * @param attribute
   * @returns unique values count
   */
  const countUnique = (attribute: string, api: GridApi): number => {
    const uniqueItemsSet: Set<any> = new Set()
    api?.forEachNodeAfterFilter((rowNode: any) => {
      if (rowNode?.data instanceof Object && attribute in rowNode.data) {
        uniqueItemsSet.add(rowNode.data[attribute])
      }
    })

    return uniqueItemsSet.size
  }

  const countYes = (attribute: string, api: GridApi): number => {
    const yesList: any[] = []
    api?.forEachNodeAfterFilter((rowNode: any) => {
      if (rowNode?.data instanceof Object && attribute in rowNode.data && rowNode.data[attribute] === 'Yes') {
        yesList.push(rowNode.data[attribute])
      }
    })

    return yesList.length
  }

  const rowClassRules = useMemo(() => {
    return {
      'is-new-warning': (params: any) => {
        const isNew = params?.data?.is_new

        return isNew === 'TRUE'
      },
      'is-new-recall': (params: any) => {
        const isNew = params?.data?.is_Recall

        return isNew === 'True'
      }
    }
  }, [])

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 100,
      maxWidth: 200
    }
  }, [])

  const getRowId = (params: any) => {
    if (params && params.data) {
      if (params.data.rowId) {
        return params.data.rowId
      }
    }

    return makeid(5)
  }

  const aggRowCal = (api: any, columnApi: any) => {
    if (!api || !columnApi) return
    const rows: any = {}
    const result = []
    columnApi?.getAllGridColumns().forEach((item: any) => {
      rows[item.colId] = null
    })
    const columnsWithAggregation = ['bucket_estimated', 'cc_allocation', 'probability_of_default_estimated']
    const columnsWithAvgAggregation = ['probability_of_default_estimated']
    columnsWithAggregation.forEach(element => {
      api?.forEachNodeAfterFilter((rowNode: any) => {
        if (rowNode.data[element]) {
          rows[element] += Number(parseFloat(rowNode.data[element]))
        }
      })
    })

    columnsWithAvgAggregation.forEach(element => {
      const nodeCount = api.getDisplayedRowCount()
      const avg = (rows[element] / nodeCount).toString()
      rows[element] = nodeCount !== 0 ? Number(parseFloat(avg).toFixed(2)) : 0
    })

    rows['isTotalRow'] = true
    rows['id'] = countUnique('id', api)
    rows['trade_blazer_account_no'] = countUnique('trade_blazer_account_no', api)
    rows['probability_of_default_cb'] = `Min: ${getMinValue('probability_of_default_cb', api)}; Max: ${getMaxValue(
      'probability_of_default_cb',
      api
    )}`
    rows['trade_blazer_account_short_name'] = countUnique('trade_blazer_account_short_name', api)
    rows['name'] = countUnique('name', api)
    rows['bucket_cb'] = countUnique('bucket_cb', api)
    rows['use_estimated'] = countYes('use_estimated', api)
    rows['parent_account'] = countUnique('parent_account', api)

    rows['credit_account'] = countUnique('credit_account', api)
    rows['inherit_parent_account_pd'] = countYes('inherit_parent_account_pd', api)
    rows['inherit_credit_account_pd'] = countYes('inherit_credit_account_pd', api)

    result.push(rows)
    api?.setPinnedBottomRowData(result)
  }

  const getMinValue = (attribute: string, api: GridApi): number => {
    const uniqueItemsSet: any[] = []
    api?.forEachNodeAfterFilter((rowNode: any) => {
      if (
        rowNode?.data instanceof Object &&
        (_.isString(rowNode.data[attribute])
          ? rowNode.data[attribute] && !_.isEmpty(rowNode.data[attribute])
          : rowNode.data[attribute])
      ) {
        uniqueItemsSet.push(+rowNode.data[attribute])
      }
    })

    return uniqueItemsSet?.length > 0 ? _.min(uniqueItemsSet) : 0
  }

  const getMaxValue = (attribute: string, api: GridApi): number => {
    const uniqueItemsSet: any[] = []
    api?.forEachNodeAfterFilter((rowNode: any) => {
      if (
        rowNode?.data instanceof Object &&
        (_.isString(rowNode.data[attribute])
          ? rowNode.data[attribute] && !_.isEmpty(rowNode.data[attribute])
          : rowNode.data[attribute])
      ) {
        uniqueItemsSet.push(+rowNode.data[attribute])
      }
    })

    return uniqueItemsSet?.length > 0 ? _.max(uniqueItemsSet) : 0
  }

  function makeid(length: any) {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    let counter = 0
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
      counter += 1
    }

    return result
  }

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: true
          }
        }
      ],
      defaultToolPanel: ''
    }
  }, [])

  function isForceRefreshSelected() {
    return (document.querySelector('#forceRefresh') as HTMLInputElement)?.checked
  }
  function isSuppressFlashSelected() {
    return (document.querySelector('#suppressFlash') as HTMLInputElement)?.checked
  }
  const onGridRowDataChanged = () => {
    const refreshParams: any = {
      force: isForceRefreshSelected(),
      suppressFlash: isSuppressFlashSelected()
    }
    gridRef.current!.api.refreshCells(refreshParams)
  }

  const onDoesfilter = useCallback((node: any) => {
    doesExternalFilterPass(node)

    return true
  }, [])

  const onAddClick = () => {
    // setIsAddDisabled(true)
    // setIsSaveDisabled(false)
    // setIsCancelDisabled(false)
    onAdd()
  }
  const onAdd = () => {
    if (onAddEvent) {
      onAddEvent?.()
    }
  }

  const onSaveClick = () => {
    gridRef.current?.api?.stopEditing()
    const rowCount = gridRef.current?.api?.getDisplayedRowCount?.()
    const isValid = true
    for (let i = 0; i < rowCount; i++) {
      const rowNode = gridRef.current.api.getDisplayedRowAtIndex(i)
      const data = rowNode?.data
      if (data?.isTotalRow) continue
    }
    if (!isValid) return
    onSave()
    refreshEvent()
  }

  const onCancelClick = () => {
    // setIsAddDisabled(false)
    // setIsSaveDisabled(true)
    // setIsCancelDisabled(true)
    onCancel()
    const rowsToKeep: any[] = []
    gridRef?.current?.api?.forEachNode((node: any) => {
      if (!node?.data?.isNewRowInsert) {
        rowsToKeep.push(node.data)
      }
    })

    gridRef?.current?.api?.setRowData(rowsToKeep)

    aggRowCal(gridRef?.current?.api, gridRef?.current?.columnApi)

    gridRef?.current?.api?.deselectAll()
  }

  useEffect(() => {
  const api = gridRef?.current?.api
  const columnApi = gridRef?.current?.columnApi
  if (api && columnApi) {
    // recompute bottom aggregation row
    aggRowCal(api, columnApi)

    try {
      // force grid to refresh visible cells
      api.refreshCells({ force: true, suppressFlash: true })
    } catch (e) {
      // ignore
    }

    // after a short delay allow grid to render then size columns
    setTimeout(() => {
      try {
        api.sizeColumnsToFit?.()
      } catch (e) {
        // ignore
      }
    }, 100)
  }
}, [rowData])

  return (
    <div className='h-100 w-100'>
      {/* <div ref={topDivRef} id='risk-by-cpty-grid' className='row'>
        <div className='col '>
          <div className='row mb-1'>
            <div className='col-12 d-flex align-items-center'>
            </div>
          </div>
        </div>
        <div className='col d-flex justify-content-end align-items-center'>
          <div className='col d-flex justify-content-end align-items-center'>
            <Button
              className='mx-1'
              size='small'
              variant='contained'
              onClick={onAddClick}
              disabled={
                isAddDisabled || !hasAccess([`${pageConstant?.RISK_BY_CPTY}_ADMIN`, `${pageConstant?.RISK_BY_CPTY}_RW`])
              }
            >
              Add
            </Button>
            <Button
              className='mx-1'
              size='small'
              variant='contained'
              disabled={
                isSaveDisabled ||
                !hasAccess([`${pageConstant?.RISK_BY_CPTY}_ADMIN`, `${pageConstant?.RISK_BY_CPTY}_RW`])
              }
              onClick={onSaveClick}
            >
              Save
            </Button>
            <Button
              className='mx-1'
              size='small'
              variant='contained'
              onClick={onCancelClick}
              disabled={
                isCancelDisabled ||
                !hasAccess([`${pageConstant?.RISK_BY_CPTY}_ADMIN`, `${pageConstant?.RISK_BY_CPTY}_RW`])
              }
            >
              Cancel
            </Button>
          </div>

        </div>
      </div> */}
      
      {isGridVisible && (
        <div className={'row' + gridTheme} style={{ height: getDivHeight('RiskByCptyGrid'), borderRadius: 5 }}>

          <AgGridReact
  ref={gridRef}
  defaultColDef={defaultColDef}
  headerHeight={26}
  rowHeight={24}
  rowStyle={{
    justifyContent: 'center',
    color: `${theme.palette.mode === 'dark' ? '' : '#2D2D2D'}`,
    fontSize: '0.75rem',
    fontWeight: '400',
    fontStyle: 'normal'
  }}
  columnDefs={columnDefs}
  getRowId={getRowId}
  onRowDataUpdated={onGridRowDataChanged}
  rowData={rowData}
  suppressRowClickSelection={true}
  suppressAggFuncInHeader={true}
  enableRangeSelection={true}
  onGridReady={onGridReady}
  rowClassRules={rowClassRules}
  autoGroupColumnDef={autoGroupColumnDef}
  groupSelectsChildren={true}
  rowGroupPanelShow='always'
  groupSelectsFiltered={true}
  sideBar={sideBar}
  animateRows={true}
  pivotMode={false}
  cacheQuickFilter={true}
  isExternalFilterPresent={isExternalFilterPresent}
  doesExternalFilterPass={onDoesfilter}
  getContextMenuItems={getContextMenuItems}
  quickFilterText={globalSearch}
  overlayLoadingTemplate={
    '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>'
  }
  onFilterChanged={onFilterChanged}
  onCellValueChanged={onCellValueChanged}
/>

        </div>
      )}
    </div>
  )
}

export default RiskByCPTYGrid
